/**
 * CharSelectState — pure cursor logic for the character-select screen.
 *
 * No Phaser import: safe to unit-test in Node/Vitest.
 * Tracks P1 (always active) and P2 (active only in co-op mode) cursors.
 */

export type PlayerSlot = 1 | 2;

export interface CharSelectStateConfig {
  /** Total entries in the hero roster. */
  heroCount: number;
  /** Roster indices that are locked (cursor skips, greyed-out). */
  lockedIndices: ReadonlyArray<number>;
  /** When true P2 must also confirm before the scene transitions. */
  coopMode: boolean;
}

interface SlotState {
  heroIndex: number;
  confirmed: boolean;
}

/** Pure cursor-management + confirm/cancel for the char-select screen. */
export class CharSelectState {
  private p1: SlotState;
  private p2: SlotState;

  constructor(private readonly cfg: CharSelectStateConfig) {
    const first = this.firstUnlocked();
    this.p1 = { heroIndex: first, confirmed: false };
    this.p2 = { heroIndex: first, confirmed: false };
  }

  // ─── Queries ───────────────────────────────────────────────────────────────

  get p1Index(): number {
    return this.p1.heroIndex;
  }
  get p2Index(): number {
    return this.p2.heroIndex;
  }
  get p1Confirmed(): boolean {
    return this.p1.confirmed;
  }
  get p2Confirmed(): boolean {
    return this.p2.confirmed;
  }

  /** True when the scene should proceed: P1 confirmed (and P2 in co-op). */
  get readyToStart(): boolean {
    if (!this.p1.confirmed) return false;
    return !this.cfg.coopMode || this.p2.confirmed;
  }

  isLocked(index: number): boolean {
    return this.cfg.lockedIndices.includes(index);
  }

  // ─── Mutations ─────────────────────────────────────────────────────────────

  /**
   * Move player cursor by dir (+1 / -1), skipping locked heroes.
   * Returns false if the player is already confirmed.
   */
  move(player: PlayerSlot, dir: number): boolean {
    const s = this.slot(player);
    if (s.confirmed) return false;
    const n = this.cfg.heroCount;
    let idx = s.heroIndex;
    for (let i = 0; i < n; i++) {
      idx = (idx + dir + n) % n;
      if (!this.isLocked(idx)) break;
    }
    s.heroIndex = idx;
    return true;
  }

  /**
   * Confirm the current selection. Returns false if hero is locked or
   * the player already confirmed.
   */
  confirm(player: PlayerSlot): boolean {
    const s = this.slot(player);
    if (s.confirmed || this.isLocked(s.heroIndex)) return false;
    s.confirmed = true;
    return true;
  }

  /** Cancel a confirmed pick (allows changing mind before both confirm). */
  cancel(player: PlayerSlot): void {
    this.slot(player).confirmed = false;
  }

  /** Reset both cursors to initial state. */
  reset(): void {
    const first = this.firstUnlocked();
    this.p1 = { heroIndex: first, confirmed: false };
    this.p2 = { heroIndex: first, confirmed: false };
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private slot(player: PlayerSlot): SlotState {
    return player === 1 ? this.p1 : this.p2;
  }

  private firstUnlocked(): number {
    for (let i = 0; i < this.cfg.heroCount; i++) {
      if (!this.isLocked(i)) return i;
    }
    return 0;
  }
}
